// libs
import React, { memo } from 'react';
import { Global, css } from '@emotion/core';

const COLORS = {
  BLACK_100: '#001325',
  BLACK_92: '#142536',
  BLACK_64: '#5c6874',
  BLACK_48: '#858E97',
  BLACK_36: '#a3aab0',
  BLACK_24: '#c2c7cb',
  BLACK_16: '#d6d9dc',
  BLACK_8: '#ebedee',
  BLACK_4: '#f3f4f5',
  BLACK_OP_100: 'rgba(0, 19, 37, 1)',
  BLACK_OP_12: 'rgba(0, 19, 37, 0.12)',
  BLACK_OP_16: 'rgba(0, 19, 37, 0.16)',
  BLACK_OP_24: 'rgba(0, 19, 37, 0.24)',
  BLACK_OP_36: 'rgba(0, 19, 37, 0.36)',
  BLACK_OP_48: 'rgba(0, 19, 37, 0.48)',
  BLACK_OP_64: 'rgba(0, 19, 37, 0.64)',
  BLACK_OP_92: 'rgba(0, 19, 37, 0.92)',
  PRIMARY_300: '#fa8bb4',
  PRIMARY_500: '#fd7685',
  YELLOW_16: 'rgba(222, 128, 0, 0.16)',
  YELLOW_8: 'rgba(222, 128, 0, 0.08)',
  ORANGE_100: '#de3800',
  BLUE_8: 'rgba(0, 109, 255, 0.08)',
  BLUE_16: 'rgba(0, 109, 255, 0.16)',
  BLUE_LITE_8: 'rgba(0, 137, 69, 0.08)',
  RED_100: '#b60000',
  RED_8: 'rgba(182, 0, 0, 0.08)',
  RED_16: 'rgba(182, 0, 0, 0.16)',
  GREEN_100: '#008945',
  GREEN_8: 'rgba(0, 137, 69, 0.08)',
  GREEN_16: 'rgba(0, 137, 69, 0.16)',
  PEBBLE: '#0e0f10',
  PEBBLE_100: 'rgba(101, 119, 134, 1)',
  PEBBLE_8: 'rgba(101, 119, 134, 0.08)',
  PEBBLE_12: 'rgba(101, 119, 134, 0.12)',
  PEBBLE_20: 'rgba(101, 119, 134, 0.2)',
  PEBBLE_28: 'rgba(101, 119, 134, 0.28)',
  PLAIN_WHITE: '#fff',
};

const BASE_FONT_SIZE = 14;

const tokens = css`
  :root {
    --font-family: Cera;
    --root-font-size: ${BASE_FONT_SIZE}px;
    --root-font-color: ${COLORS.BLACK_100};
    --root-font-weight: 400;

    /* Font Sizes */
    --font-size-10: ${10 / BASE_FONT_SIZE}rem;
    --font-size-11: ${11 / BASE_FONT_SIZE}rem;
    --font-size-12: ${12 / BASE_FONT_SIZE}rem;
    --font-size-13: ${13 / BASE_FONT_SIZE}rem;
    --font-size-14: ${14 / BASE_FONT_SIZE}rem;
    --font-size-15: ${15 / BASE_FONT_SIZE}rem;
    --font-size-16: ${16 / BASE_FONT_SIZE}rem;
    --font-size-17: ${17 / BASE_FONT_SIZE}rem;
    --font-size-18: ${18 / BASE_FONT_SIZE}rem;
    --font-size-19: ${19 / BASE_FONT_SIZE}rem;
    --font-size-20: ${20 / BASE_FONT_SIZE}rem;
    --font-size-24: ${24 / BASE_FONT_SIZE}rem;

    /** Colors */
    --color-black-100: ${COLORS.BLACK_100};
    --color-black-92: ${COLORS.BLACK_92};
    --color-black-64: ${COLORS.BLACK_64};
    --color-black-48: ${COLORS.BLACK_48};
    --color-black-36: ${COLORS.BLACK_36};
    --color-black-24: ${COLORS.BLACK_24};
    --color-black-16: ${COLORS.BLACK_16};
    --color-black-8: ${COLORS.BLACK_8};

    --color-black-op-100: ${COLORS.BLACK_OP_100};
    --color-black-op-12: ${COLORS.BLACK_OP_12};
    --color-black-op-16: ${COLORS.BLACK_OP_16};
    --color-black-op-24: ${COLORS.BLACK_OP_24};
    --color-black-op-36: ${COLORS.BLACK_OP_36};
    --color-black-op-48: ${COLORS.BLACK_OP_48};
    --color-black-op-64: ${COLORS.BLACK_OP_64};
    --color-black-op-92: ${COLORS.BLACK_OP_92};

    --color-yellow-16: ${COLORS.YELLOW_16};
    --color-yellow-8: ${COLORS.YELLOW_8};
    --color-orange-100: ${COLORS.ORANGE_100};
    --color-red-100: ${COLORS.RED_100};
    --color-red-8: ${COLORS.RED_8};
    --color-red-16: ${COLORS.RED_16};
    --color-green-100: ${COLORS.GREEN_100};
    --color-green-8: ${COLORS.GREEN_8};
    --color-green-16: ${COLORS.GREEN_16};
    --color-blue-8: ${COLORS.BLUE_8};
    --color-blue-16: ${COLORS.BLUE_16};
    --color-blue-lite-8: ${COLORS.BLUE_LITE_8};

    --color-pink-300: ${COLORS.PRIMARY_300};
    --color-primary-500: ${COLORS.PRIMARY_500};

    --color-pebble-100: ${COLORS.PEBBLE_100};
    --color-pebble-8: ${COLORS.PEBBLE_8};
    --color-pebble-12: ${COLORS.PEBBLE_12};
    --color-pebble-20: ${COLORS.PEBBLE_20};
    --color-pebble-28: ${COLORS.PEBBLE_28};

    --color-pebble: ${COLORS.PEBBLE};

    --color-plain-white: ${COLORS.PLAIN_WHITE};

    /** Font colors */
    --font-alert-error: ${COLORS.RED_100};
    --font-alert-success: ${COLORS.GREEN_100};
    --font-alert-info: ${COLORS.ORANGE_100};
    --font-disabled: ${COLORS.BLACK_36};

    /** Spacings */
    --base-gutter: 8px;
    --page-gutter: 20px;
    --page-left-gutter: 20px;
    --page-right-gutter: 20px;

    --header-height: 56px;
    --cart-cta-height: 80px;

    --desktop-header-height: 85px;
    --desktop-footer-height: 60px;
    --desktop-layout-margin: 145px;
    --desktop-max-width: 1200px;

    --toast-bg-color: ${COLORS.PEBBLE};

    --overlay-z-index: 999;

    /** spacing */
    --space-0-0-16: 0 0 16px 0;
    --space-8-0-4: 8px 0 4px 0;
    --space-32-20: 32px 20px 0;
    --space-bottom-30: 16px 16px 30px 16px;
    --space-20-16: 16px 20px;
  }
`;

function StyleTokens() {
  return <Global styles={tokens} />;
}

export default memo(StyleTokens);
